/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  Building2, MapPin, Phone, Mail, Globe, Calendar, ArrowLeft, Star,
  Users, Camera, ExternalLink, User
} from 'lucide-react';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveImage = (url) => resolveImg(url);

/* Shared card wrapper for consistent styling */
const Card = ({ children, className = '', ...props }) => (
  <div className={`bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${className}`} {...props}>{children}</div>
);
const CardHeader = ({ title, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon && <Icon className="w-[15px] h-[15px] text-[#002249]/50" />}
    <h2 className="text-[14px] font-bold text-[#1A1A1A] tracking-[-0.01em]">{title}</h2>
  </div>
);

const BusinessProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/portal/business/${userId}`, { withCredentials: true });
        setData(res.data);
      } catch { navigate(-1); }
      finally { setLoading(false); }
    };
    load();
  }, [userId, navigate]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>;
  if (!data) return null;

  const { profile: cp, owner, representatives, membership_plan: plan, is_founding_member, stats } = data;
  const isOwner = currentUser?.id === userId;

  return (
    <div className="min-h-screen bg-[#F0F2F5]" data-testid="business-profile">
      {/* Banner — corporate dark navy */}
      <div className="relative h-36 sm:h-44 lg:h-48 overflow-hidden" data-testid="business-banner">
        <div className="absolute inset-0 bg-[#0C1A2E]">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'80\' height=\'80\' viewBox=\'0 0 80 80\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Crect x=\'0\' y=\'0\' width=\'80\' height=\'80\' fill=\'none\' stroke=\'%23fff\' stroke-width=\'1\'/%3E%3Crect x=\'20\' y=\'20\' width=\'40\' height=\'40\' fill=\'none\' stroke=\'%23fff\' stroke-width=\'0.5\'/%3E%3C/svg%3E")' }} />
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E63946] via-[#E63946]/60 to-transparent" />
        </div>
        <button onClick={() => navigate(-1)} className="absolute top-5 left-5 flex items-center gap-1.5 text-white/70 hover:text-white text-[13px] font-medium bg-white/8 hover:bg-white/15 backdrop-blur-sm px-3.5 py-2 rounded-lg transition-all z-10" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="absolute top-5 right-5 z-10">
          <span className="flex items-center gap-1.5 bg-white/8 backdrop-blur-sm text-white/60 text-[11px] font-semibold px-3 py-1.5 rounded-lg tracking-wide">
            <Building2 className="w-3.5 h-3.5" /> Business Profile
          </span>
        </div>
      </div>

      {/* Content container */}
      <div className="max-w-[920px] mx-auto px-5 sm:px-8">
        {/* Header: Logo + Company Info */}
        <div className="-mt-14 sm:-mt-16 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6">
            {/* Logo */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-[3px] border-white shadow-lg bg-white flex items-center justify-center overflow-hidden flex-shrink-0 relative z-10" data-testid="company-logo">
              {cp.logo_url ? (
                <Img variant="business" src={resolveImage(cp.logo_url)} alt={cp.company_name} className="w-full h-full object-contain p-2.5" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#002249] to-[#0a3570] flex items-center justify-center">
                  <Building2 className="w-10 h-10 text-white/40" />
                </div>
              )}
            </div>
            {/* Info — pushed down so name is fully below the banner */}
            <div className="flex-1 min-w-0 sm:pt-16">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <h1 className="text-[22px] sm:text-[28px] font-bold text-[#1A1A1A] leading-tight tracking-[-0.02em]" data-testid="company-name">{cp.company_name}</h1>
                  <div className="flex items-center flex-wrap gap-2 mt-2.5">
                    {is_founding_member && (
                      <span className="bg-amber-50 text-amber-700 border border-amber-200/80 px-2.5 py-[3px] rounded-full font-semibold text-[11px] flex items-center gap-1 tracking-wide" data-testid="founding-badge">
                        <Star className="w-3 h-3 fill-current" /> Founding Member
                      </span>
                    )}
                    {plan && <span className="bg-[#002249]/6 text-[#002249] px-2.5 py-[3px] rounded-full font-semibold text-[11px]">{plan.name}</span>}
                    {stats.joined && <span className="text-[12px] text-[#8C8C8C] flex items-center gap-1"><Calendar className="w-3 h-3" />Since {new Date(stats.joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                  </div>
                </div>
                {/* Personal profile link */}
                {isOwner && (
                  <Link to={`/portal/members/${userId}`} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#002249] border border-[#002249]/15 bg-white hover:bg-[#002249] hover:text-white px-4 py-2.5 rounded-lg transition-all shadow-sm flex-shrink-0" data-testid="view-personal-profile">
                    <User className="w-3.5 h-3.5" /> Personal Profile
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Grid: Main + Sidebar */}
        <div className="grid lg:grid-cols-3 gap-6 pb-10">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* About */}
            {cp.description && (
              <Card className="p-6" data-testid="about-section">
                <CardHeader title="About" />
                <p className="text-[14px] text-[#555] leading-[1.7] whitespace-pre-line">{cp.description}</p>
              </Card>
            )}

            {/* Gallery */}
            {cp.gallery_images?.length > 0 && (
              <Card className="p-6" data-testid="gallery-section">
                <CardHeader title="Gallery" icon={Camera} />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  {cp.gallery_images.map((img, i) => (
                    <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-50">
                      <Img variant="generic" src={resolveImage(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Team */}
            {(owner || representatives.length > 0) && (
              <Card className="p-6" data-testid="team-section">
                <CardHeader title="Team" icon={Users} />
                <div className="space-y-1">
                  {owner && (
                    <Link to={`/portal/members/${owner.id}`} className="flex items-center gap-3.5 px-3 py-3 -mx-1 rounded-lg hover:bg-slate-50/80 transition-colors" data-testid="owner-link">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#E63946] to-[#F4A261] flex items-center justify-center text-white font-bold text-[13px] flex-shrink-0">
                        {owner.picture ? <Img variant="avatar" src={resolveImage(owner.picture)} alt="" className="w-full h-full object-cover" /> : owner.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-[#1A1A1A]">{owner.name}</p>
                        <p className="text-[11px] text-[#E63946] font-medium mt-0.5">Owner / Primary Contact</p>
                      </div>
                    </Link>
                  )}
                  {representatives.map(rep => (
                    <Link key={rep.id} to={`/portal/members/${rep.id}`} className="flex items-center gap-3.5 px-3 py-3 -mx-1 rounded-lg hover:bg-slate-50/80 transition-colors" data-testid={`rep-${rep.id}`}>
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#002249] to-[#457B9D] flex items-center justify-center text-white font-bold text-[13px] flex-shrink-0">
                        {rep.picture ? <Img variant="avatar" src={resolveImage(rep.picture)} alt="" className="w-full h-full object-cover" /> : rep.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-[#1A1A1A]">{rep.name}</p>
                        {rep.roles?.length > 0 && <p className="text-[11px] text-[#8C8C8C] mt-0.5">{rep.roles.join(' / ')}</p>}
                      </div>
                    </Link>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact */}
            <Card className="p-6" data-testid="contact-card">
              <CardHeader title="Contact" icon={Mail} />
              <div className="space-y-3.5">
                {cp.email && (
                  <a href={`mailto:${cp.email}`} className="flex items-center gap-3 text-[13px] text-[#555] hover:text-[#002249] transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0"><Mail className="w-3.5 h-3.5 text-slate-400" /></div>
                    <span className="truncate">{cp.email}</span>
                  </a>
                )}
                {cp.phone && (
                  <a href={`tel:${cp.phone}`} className="flex items-center gap-3 text-[13px] text-[#555] hover:text-[#002249] transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0"><Phone className="w-3.5 h-3.5 text-slate-400" /></div>
                    <span>{cp.phone}</span>
                  </a>
                )}
                {cp.website && (
                  <a href={cp.website.startsWith('http') ? cp.website : `https://${cp.website}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-[13px] text-[#555] hover:text-[#002249] transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0"><Globe className="w-3.5 h-3.5 text-slate-400" /></div>
                    <span className="truncate">{cp.website}</span>
                    <ExternalLink className="w-3 h-3 text-slate-300 flex-shrink-0" />
                  </a>
                )}
                {cp.address && (
                  <div className="flex items-start gap-3 text-[13px] text-[#555]">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0"><MapPin className="w-3.5 h-3.5 text-slate-400" /></div>
                    <span className="leading-relaxed">{cp.address}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Details */}
            <Card className="p-6" data-testid="details-card">
              <CardHeader title="Details" icon={Building2} />
              <div className="space-y-3">
                {[
                  cp.industry && { label: 'Industry', value: cp.industry.replace('_', ' ') },
                  plan && { label: 'Membership', value: plan.name },
                  stats.representatives > 0 && { label: 'Team', value: `${stats.representatives} member${stats.representatives > 1 ? 's' : ''}` },
                  stats.gallery_images > 0 && { label: 'Photos', value: stats.gallery_images },
                ].filter(Boolean).map(row => (
                  <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-slate-100/80 last:border-0">
                    <span className="text-[13px] text-[#8C8C8C]">{row.label}</span>
                    <span className="text-[13px] text-[#1A1A1A] font-medium capitalize">{row.value}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Social */}
            {(cp.social_facebook || cp.social_instagram || cp.social_linkedin) && (
              <Card className="p-6">
                <CardHeader title="Social" icon={Globe} />
                <div className="flex gap-2.5">
                  {cp.social_facebook && <a href={cp.social_facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500 hover:bg-blue-100 transition-colors"><Globe className="w-4 h-4" /></a>}
                  {cp.social_instagram && <a href={cp.social_instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center text-pink-500 hover:bg-pink-100 transition-colors"><Camera className="w-4 h-4" /></a>}
                  {cp.social_linkedin && <a href={cp.social_linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center text-sky-500 hover:bg-sky-100 transition-colors"><Globe className="w-4 h-4" /></a>}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;
